import { RedditReportsQuery } from 'wb-reddit/graphql/generated';
import {
	ArrayElement, Avatar, AvatarSize, Counter, DATE_DISPLAY_FORMAT, isReddit,
	isSpotify,
	MiddleEllipsisWhisper,
} from '@aleph/shared-components';
import React, { FC, forwardRef } from 'react';
import dayjs from 'dayjs';
import { REDDIT_CATEGORIZED_COLUMNS } from 'wb-reddit/app/routes/utils';
import {
	getReportClientIds,
	getScheduleButtonName,
	RedditAccessEntity,
	ReportRow,
	SCHEDULER_OPTIONS,
} from 'wb-reddit/app/routes/reports/utils';
import RedditReportsActions from 'wb-reddit/app/routes/reports/components/actions';
import classNames from 'classnames';
import useReportActionsOpen from 'wb-reddit/app/routes/reports/hooks/useReportActionsOpen';
import RedditAccessImages from 'wb-reddit/app/routes/reports/components/accessImages';
import { HandleUpdateRow } from 'wb-reddit/app/routes/hooks/useUpdateRows';
import { fallBackUserImage } from 'wb-reddit/app/common/components/entityImage';
import { useTranslation } from 'react-i18next';
import { SpotifyReportsQueryItems } from 'wb-spotify/graphql/generated';
import { SpotifyReportSchedule } from 'wb-spotify/src/app/common/utils';
import useSupergridSettings from 'wb-spotify/app/routes/superGrid/hooks/useSupergridSettings';
import {
	SPOTIFY_SUPERGRID_STATISTICS_METRICS_CATEGORIZED,
	SPOTIFY_SUPERGRID_STATISTICS_METRICS_LIFETIME_CATEGORIZED,
} from 'wb-spotify/app/routes/superGrid/statistics/utils';
import useSetCreateRedditReportData from 'wb-reddit/app/routes/reports/hooks/useSetCreateRedditReportData';
import { useNavigate } from 'react-router-dom';

export type RedditReport = ArrayElement<RedditReportsQuery['reports']['items']>;

interface IProps {
	reportItem: RedditReport & { reportImage?: string }
	handleUpdateRow: HandleUpdateRow<ReportRow>
}

// TODO separate into reddit/spotify
const RedditReportListItem: FC<IProps> = forwardRef<HTMLDivElement, IProps>(({ reportItem, handleUpdateRow, ...rest }, ref) => {
	const { t } = useTranslation();
	const { lifetimeStats } = useSupergridSettings();
	const navigate = useNavigate();

	const getMetricsCategory = (metrics: (string | null)[]) => {
		const selectedCategory = (isReddit()
			? REDDIT_CATEGORIZED_COLUMNS
			: (lifetimeStats ? SPOTIFY_SUPERGRID_STATISTICS_METRICS_LIFETIME_CATEGORIZED : SPOTIFY_SUPERGRID_STATISTICS_METRICS_CATEGORIZED)
		)
			.find(category => category.metrics.every(metric => metrics.includes(metric.value)));

		if (selectedCategory?.metrics?.length === metrics?.length) {
			return selectedCategory?.label;
		}

		return t('sc: chart custom', 'Custom');
	};

	const { reportActionsOpenId } = useReportActionsOpen();

	const getScheduleName = (report: RedditReport) => {
		if (isSpotify()) {
			// Change the type of reportItem to SpotifyReport
			const spotifyReport = report as unknown as SpotifyReportsQueryItems;

			if (spotifyReport.reportSchedule === SpotifyReportSchedule.OnlyOnce) {
				return `${dayjs(spotifyReport.reportDateRangeOptions?.dateRangeValue?.[0]).format(DATE_DISPLAY_FORMAT)} - ${dayjs(spotifyReport.reportDateRangeOptions?.dateRangeValue?.[1]).format(DATE_DISPLAY_FORMAT)}`;
			}

			if (spotifyReport.reportSchedule === SpotifyReportSchedule.EveryMonday) {
				return 'Every Monday';
			}

			if (spotifyReport.reportSchedule === SpotifyReportSchedule.EveryWeekday) {
				return 'Every Weekday';
			}

			if (spotifyReport.reportSchedule === SpotifyReportSchedule.FirstOfTheMonth) {
				return 'First of the Month';
			}

			return 'Custom';
		}

		const selectedSchedule = SCHEDULER_OPTIONS.find(scheduleOption => scheduleOption.value === report?.reportDateType)?.label;

		if (selectedSchedule) {
			const scheduleButtonName = getScheduleButtonName(report?.reportDateType ?? undefined);

			return (
				<>
					<strong>
						{selectedSchedule}
					</strong>
					&nbsp;
					{scheduleButtonName ? `on ${scheduleButtonName}` : `at ${report.reportSchedule}`}
				</>
			);
		}

		const dateRangeValues = report.reportDateRangeOptions?.dateRangeValue ?? [];

		return (
			<>
				{dayjs(dateRangeValues?.[0]).format(DATE_DISPLAY_FORMAT)} - {dayjs(dateRangeValues?.[1]).format(DATE_DISPLAY_FORMAT)}
			</>
		);
	};

	const startReportEdit = useSetCreateRedditReportData();

	function onReportEdit() {
		if (isSpotify()) {
			navigate(`/reports/${reportItem?.reportId}`);
		} else {
			startReportEdit(reportItem);
		}
	}

	return (
		<div className="border-b border-b-gray-200 whitespace-nowrap">
			<div
				key={reportItem?.reportId}
				{...rest}
				ref={ref}
				className={
					classNames(
						'flex h-[82px] transition-[opacity] group items-center text-black '
						+ 'hover:bg-gray-100 hover:opacity-100',
						{
							'opacity-40':  reportActionsOpenId && reportActionsOpenId !== reportItem?.reportId,
							'bg-gray-100': reportActionsOpenId && reportActionsOpenId === reportItem?.reportId,
						},
					)
				}
				data-testid={`reportentry-${reportItem?.reportId}`}
			>
				<div data-testid="reportsid"
					className="flex-1 px-[8px] text-[16px] gap-[16px] overflow-hidden
						group group-hover:text-blue cursor-pointer flex items-center"
					onClick={onReportEdit}
				>
					<div
						className={classNames({
							'opacity-20': !reportItem.reportReady,
						})}
						data-testid={reportItem.reportReady ? 'activated' : 'paused'}
					>
						<Avatar
							avatarSource={reportItem.reportImage || fallBackUserImage()}
							size={AvatarSize.Medium}
						/>
					</div>
					<div className="overflow-hidden font-medium w-full">
						<MiddleEllipsisWhisper value={reportItem?.reportName} />
					</div>
				</div>
				<div className="relative w-[80px] flex items-center justify-center">
					<Counter
						count={
							getReportClientIds(reportItem?.reportApi?.reportApiFiltersOptions).length
						}
						showZero
					/>
				</div>
				<div className="px-[8px] w-[220px] text-gray-800">
					{getScheduleName(reportItem)}
				</div>
				<div className="px-[8px] w-[180px] text-gray-800">{
					getMetricsCategory(reportItem?.reportApi?.reportApiMetrics ?? [])
				}</div>
				{isReddit() && (
					<div className="px-[8px] w-[160px]">
						<RedditAccessImages
							accessEntities={
								[
									...(reportItem?.users ?? []),
									...(reportItem?.userGroups ?? []),
								] as RedditAccessEntity[]
							}
						/>
					</div>
				)}
				<div className="px-[8px] w-[260px]">
					<RedditReportsActions
						report={reportItem}
						handleUpdateRow={handleUpdateRow}
					/>
				</div>
			</div>
		</div>
	);
});

export default RedditReportListItem;
